import { useField, useFormikContext } from 'formik';
import React from 'react';

import styled from '@emotion/styled';

import CheckMarkIcon from '../Icons/CheckMarkIcon';
import { FieldErrorInfo, IInputField } from './FormField';

interface CustomCheckboxGroupProps {
  children: React.ReactNode;
  name: string;
  label?: string;
  doNotShowError?: boolean;
  className?: string;
}

export const CustomCheckboxGroup: React.FC<CustomCheckboxGroupProps> = ({
  children,
  name,
  label,
  doNotShowError,
  className,
}) => {
  const { errors, touched } = useFormikContext<any>();

  const group = (
    <CheckboxGroup className={className}>
      {React.Children.map(children, (child: any) => {
        return React.cloneElement(child, {
          ...child.props,
          name,
        });
      })}

      {!doNotShowError && errors[name] && touched[name] ? (
        <CustomFieldErrorInfo className={className}>
          {(errors as any)[name]?.capitalize() ?? ''}
        </CustomFieldErrorInfo>
      ) : null}
    </CheckboxGroup>
  );

  if (!label) return group;

  return (
    <GroupContainer>
      <GroupLabel>{label}</GroupLabel>
      {group}
    </GroupContainer>
  );
};

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GroupLabel = styled.label`
  display: flex;
  font-size: 14px;
  font-weight: 600;
`;

const CustomFieldErrorInfo = styled(FieldErrorInfo)`
  margin-top: 12px;
  margin-left: 12px;

  &.slim-error {
    margin-top: 0px;
    margin-left: 0px;
  }
`;

export const CustomCheckbox: React.FC<
  Pick<IInputField, 'id' | 'className'> & {
    value?: string;
    name?: string;
    children?: string;
  }
> = ({ children, className, ...props }) => {
  const [field] = useField({ ...props, type: 'checkbox' });

  return (
    <CheckboxWrapper className={className} notChildren={!children}>
      <label>
        <input type='checkbox' {...field} {...props} />
        {children && children}
        <span>
          <CheckMarkIcon />
        </span>
      </label>
    </CheckboxWrapper>
  );
};

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  &.row-group {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

interface CheckboxWrapperProps {
  notChildren?: boolean;
}

const CheckboxWrapper = styled.div<CheckboxWrapperProps>`
  display: ${({ notChildren }) => (notChildren ? 'inline' : 'flex')};
  gap: 12px;
  width: ${({ notChildren }) => (notChildren ? 'fit-content' : '100%')};

  & > label {
    position: relative;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: black;
    user-select: none;
  }

  & > label input[type='checkbox'] {
    margin-right: 18px;
  }

  & > label input[type='checkbox'] ~ span {
    content: '';
    width: 20px;
    height: 20px;
    background-color: white;
    color: white;
    border: 1px solid ${({ theme }) => theme.palette.borderColor};
    /* border: 1px solid black; */
    border-radius: 4px;
    position: absolute;
    left: 0;
  }

  & > label:hover input[type='checkbox'] ~ span {
    border: 1px solid ${({ theme }) => theme.palette.darkBorderColor};
  }

  & > label input[type='checkbox']:checked ~ span {
    background-color: ${({ theme }) => theme.palette.blueColor};
    border: 1px solid ${({ theme }) => theme.palette.blueColor};
  }

  & > * {
    cursor: pointer;
  }
`;
